import { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Loader2, Filter, TrendingDown, Users2, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = {
  "Propuesta": "from-emerald-600 to-emerald-500",
  "Validación": "from-teal-600 to-teal-500",
  "Conceptualización": "from-cyan-600 to-cyan-500",
  "Desarrollo": "from-blue-600 to-blue-500",
  "Finalizado": "from-violet-700 to-violet-500",
};

/**
 * Widget del Funnel de Innovación:
 *  - Barras horizontales por etapa Propuesta → Validación → Conceptualización → Desarrollo → Finalizado
 *  - Contadores por origen (PG-07 vs Iniciativas) y drop-off
 *  - Panel lateral con visitas de innovation.valorizasm.com (últimos 30d)
 */
export function InnovationFunnelWidget({ token }) {
  const [funnel, setFunnel] = useState(null);
  const [visits, setVisits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let c = false;
    (async () => {
      setLoading(true);
      try {
        const [f, v] = await Promise.all([
          fetch(`${API}/innovation-funnel/counts`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
          fetch(`${API}/innovation-funnel/site-visits?days=30`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        ]);
        if (!c) { setFunnel(f); setVisits(v); }
      } catch { /* silent */ }
      finally { if (!c) setLoading(false); }
    })();
    return () => { c = true; };
  }, [token]);

  if (loading) return <Card><CardContent className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></CardContent></Card>;
  if (!funnel) return null;

  const max = Math.max(...Object.values(funnel.counts));
  const pg07Sum = Object.values(funnel.detail).reduce((a, d) => a + (d.pg07 || 0), 0);
  const initiativesSum = Object.values(funnel.detail).reduce((a, d) => a + (d.initiatives || 0), 0);

  return (
    <Card data-testid="innovation-funnel-widget">
      <CardContent className="p-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Innovation OS · Funnel</div>
            <h3 className="text-lg font-bold flex items-center gap-2"><Filter className="w-4 h-4" /> Flujo de Innovación</h3>
            <p className="text-xs text-muted-foreground" data-testid="funnel-widget-total-note">
              {funnel.total} iniciativas totales entre PG-07 + Portafolio
              {" "}({pg07Sum} de PG-07 + {initiativesSum} de Portfolio — excluye canceladas/sin fase mapeada, por eso no es 57 ni 170)
            </p>
          </div>
          {visits && (
            <div className="text-right text-xs" data-testid="funnel-visits">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1 justify-end">
                <Eye className="w-3 h-3" /> innovation.valorizasm.com · 30d
              </div>
              <div className="text-2xl font-black text-slate-700 dark:text-slate-200">{visits.total_visits.toLocaleString("es-ES")}</div>
              <div className="text-[11px] text-muted-foreground">{visits.unique_sessions} sesiones únicas</div>
            </div>
          )}
        </div>

        <div className="space-y-2" data-testid="funnel-stages">
          {funnel.stages.map((s, i) => {
            const count = funnel.counts[s] || 0;
            const width = max ? Math.max(6, (count / max) * 100) : 0;
            const conv = funnel.conversion_pct[s];
            const detail = funnel.detail[s] || {};
            return (
              <div key={s} data-testid={`funnel-stage-${i}`}>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 w-36">{s}</span>
                  <span className="text-2xl font-black text-slate-800 dark:text-white">{count}</span>
                  {conv != null && conv < 100 && (
                    <span className="text-[10px] text-red-600 dark:text-red-400 inline-flex items-center gap-0.5">
                      <TrendingDown className="w-3 h-3" /> {conv}% conversión
                    </span>
                  )}
                  {(detail.pg07 > 0 || detail.initiatives > 0) && (
                    <span className="ml-auto text-[10px] text-muted-foreground">
                      {detail.pg07 > 0 && <span>{detail.pg07} PG-07</span>}
                      {detail.pg07 > 0 && detail.initiatives > 0 && <span> · </span>}
                      {detail.initiatives > 0 && <span>{detail.initiatives} portfolio</span>}
                    </span>
                  )}
                </div>
                <div className="h-6 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${STAGE_COLORS[s]} transition-all duration-500 shadow-inner`}
                       style={{ width: `${width}%` }}
                       data-testid={`funnel-bar-${i}`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {visits?.top_paths?.length > 0 && (
          <div className="mt-5 pt-4 border-t">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1">
              <Users2 className="w-3 h-3" /> Páginas más visitadas
            </div>
            <div className="space-y-1">
              {visits.top_paths.slice(0, 5).map((p, i) => (
                <div key={p.path} className="flex items-center gap-2 text-xs" data-testid={`funnel-top-path-${i}`}>
                  <span className="text-muted-foreground w-4">{i+1}</span>
                  <span className="truncate flex-1 font-mono text-[11px]">{p.path}</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-300">{p.visits}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <BeaconSnippet />
      </CardContent>
    </Card>
  );
}

function BeaconSnippet() {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/innovation-funnel/beacon.js`;
  const snippet = `<script src="${src}" async></script>`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* silent */ }
  };
  return (
    <div className="mt-5 pt-4 border-t">
      <button
        type="button"
        className="text-[11px] text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-1"
        onClick={() => setOpen(v => !v)}
        data-testid="beacon-snippet-toggle"
      >
        {open ? "▾" : "▸"} Snippet para innovation.valorizasm.com
      </button>
      {open && (
        <div className="mt-2 p-3 rounded bg-slate-900 dark:bg-slate-950 text-slate-100 text-[11px] font-mono relative" data-testid="beacon-snippet-block">
          <button
            type="button"
            onClick={copy}
            className="absolute top-1 right-1 text-[10px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
            data-testid="beacon-snippet-copy"
          >
            {copied ? "✓ Copiado" : "Copiar"}
          </button>
          <pre className="whitespace-pre-wrap break-all pr-16">{snippet}</pre>
          <div className="text-[10px] text-slate-400 mt-2">Pega antes de {"</body>"} en el sitio público. Rastrea páginas iniciales + navegación SPA.</div>
        </div>
      )}
    </div>
  );
}
